import { Injectable } from '@angular/core';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { GeoLocation } from './location.model';
import { environment } from '../../../environments/environment';

export interface GeocodeMatch {
  geo: GeoLocation;
  /** The building itself or the house number on the street, not a partial / area match. */
  exact: boolean;
  /** Google's formatted address, shown to the user when the match isn't exact. */
  matchedAddress: string;
}

// Customers navigate to this pin: only the building (ROOFTOP) or the house number placed
// along the street (RANGE_INTERPOLATED) is precise enough. Unknown streets still return a
// result, matched to a street centre or the city (GEOMETRIC_CENTER / APPROXIMATE).
const EXACT_LOCATION_TYPES = new Set<string>(['ROOFTOP', 'RANGE_INTERPOLATED']);

@Injectable({ providedIn: 'root' })
export class GeocodingService {
  // Loaded once, then reused for every geocode call.
  #geocoder?: Promise<google.maps.Geocoder>;

  constructor() {
    setOptions({ key: environment.googleMapsApiKey, v: 'weekly' });
  }

  /**
   * Geocode an address via the Maps JavaScript API's Geocoder (works with the same
   * HTTP-referer-restricted key as the embedded map).
   * Returns null when nothing was found or the lookup failed; the reason is logged.
   */
  async geocode(address: string): Promise<GeocodeMatch | null> {
    try {
      const geocoder = await this.#getGeocoder();
      const { results: [best] } = await geocoder.geocode({ address });
      if (!best) {
        console.warn(`Geocoding returned no results for "${address}"`);
        return null;
      }
      const { location, location_type } = best.geometry;
      return {
        geo: { latitude: location.lat(), longitude: location.lng() },
        exact: !best.partial_match && EXACT_LOCATION_TYPES.has(location_type),
        matchedAddress: best.formatted_address,
      };
    } catch (error) {
      // MapsRequestError.code is the GeocoderStatus (ZERO_RESULTS, REQUEST_DENIED, OVER_QUERY_LIMIT, ...)
      console.warn(`Geocoding failed for "${address}"`, error);
      return null;
    }
  }

  #getGeocoder(): Promise<google.maps.Geocoder> {
    this.#geocoder ??= importLibrary('geocoding').then(({ Geocoder }) => new Geocoder());
    return this.#geocoder;
  }
}
